import React, { useState, useEffect, useCallback } from 'react';
import { format, differenceInMinutes } from 'date-fns';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { QRCodeSVG } from 'qrcode.react';
import { db } from '../services/firebase';

const BOOKING_URL = 'https://thehubcrookwell.com.au/book?space=meeting-room';

const PASSCODE = '2580';
const STORAGE_KEY = 'meeting-room-display-unlocked';
const REFRESH_INTERVAL = 60000; // 60 seconds
const TICK_INTERVAL = 1000; // update clock every second

const toDate = (v) => {
  if (!v) return null;
  if (v.toDate) return v.toDate();
  return new Date(v);
};

const firstNameLastInitial = (b) => {
  const first = b.firstName || '';
  const last = b.lastName || '';
  return last ? `${first} ${last[0]}.` : first || 'Booked';
};

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const endOfDay = (d) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

const MeetingRoomDisplayPage = () => {
  const [unlocked, setUnlocked] = useState(() => localStorage.getItem(STORAGE_KEY) === 'true');
  const [passcodeInput, setPasscodeInput] = useState('');
  const [passcodeError, setPasscodeError] = useState(false);
  const [now, setNow] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Clock tick (every second)
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), TICK_INTERVAL);
    return () => clearInterval(t);
  }, []);

  const loadBookings = useCallback(async () => {
    try {
      const today = new Date();
      const dayStart = startOfDay(today);
      const dayEnd = endOfDay(today);

      const q = query(
        collection(db, 'bookings'),
        where('spaceId', '==', 'meeting-room'),
      );
      const snap = await getDocs(q);
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((b) => {
          if (b.status === 'cancelled') return false;
          if (b.type === 'guest') return false;
          const start = toDate(b.startDate);
          return start && start >= dayStart && start <= dayEnd;
        })
        .sort((a, b) => toDate(a.startDate) - toDate(b.startDate));

      setBookings(list);
    } catch (err) {
      console.error('Failed to load bookings:', err);
    }
    setLoading(false);
  }, []);

  // Fetch bookings on unlock + every 60s
  useEffect(() => {
    if (!unlocked) return;
    loadBookings();
    const t = setInterval(loadBookings, REFRESH_INTERVAL);
    return () => clearInterval(t);
  }, [unlocked, loadBookings]);

  // Request wake lock (prevent screen sleep)
  useEffect(() => {
    if (!unlocked) return;
    let wakeLock = null;
    const request = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
        }
      } catch { /* wake lock not supported or denied */ }
    };
    request();
    const onVisibility = () => { if (document.visibilityState === 'visible') request(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (wakeLock) wakeLock.release().catch(() => {});
    };
  }, [unlocked]);

  // Passcode unlock
  const handleUnlock = (e) => {
    e.preventDefault();
    if (passcodeInput === PASSCODE) {
      localStorage.setItem(STORAGE_KEY, 'true');
      setUnlocked(true);
      setPasscodeError(false);
    } else {
      setPasscodeError(true);
      setPasscodeInput('');
    }
  };

  const handleLock = () => {
    if (window.confirm('Lock display?')) {
      localStorage.removeItem(STORAGE_KEY);
      setUnlocked(false);
    }
  };

  // Passcode screen
  if (!unlocked) {
    return (
      <div className="fixed inset-0 bg-slate-900 flex items-center justify-center z-50">
        <div className="w-full max-w-sm px-8 text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Meeting Room Display</h1>
          <p className="text-slate-400 mb-8">Enter passcode to unlock</p>
          <form onSubmit={handleUnlock}>
            <input
              type="password"
              inputMode="numeric"
              autoFocus
              value={passcodeInput}
              onChange={(e) => { setPasscodeInput(e.target.value); setPasscodeError(false); }}
              className={`w-full text-center text-4xl tracking-[0.5em] py-4 rounded-xl bg-slate-800 text-white border-2 ${passcodeError ? 'border-red-500' : 'border-slate-700'} focus:border-brand-500 focus:outline-none`}
              maxLength={4}
              placeholder="••••"
            />
            {passcodeError && <p className="mt-3 text-sm text-red-400">Incorrect passcode</p>}
            <button
              type="submit"
              disabled={passcodeInput.length !== 4}
              className="mt-6 w-full py-3 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-xl disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Unlock
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Determine current and next booking
  const current = bookings.find((b) => {
    const start = toDate(b.startDate);
    const end = toDate(b.endDate);
    return start && end && now >= start && now < end;
  });

  const upcoming = bookings.filter((b) => {
    const start = toDate(b.startDate);
    return start && start > now;
  });
  const next = upcoming[0];

  const available = !current;

  // Minutes until next state change
  const minutesUntil = (d) => Math.max(0, differenceInMinutes(d, now));

  return (
    <div className="fixed inset-0 bg-slate-900 text-white overflow-hidden flex flex-col">
      {/* Top bar: clock + date + lock button */}
      <div className="flex items-start justify-between px-10 pt-8">
        <div>
          <div className="text-7xl font-bold tabular-nums tracking-tight">
            {format(now, 'h:mm')}<span className="text-slate-400 text-5xl ml-3">{format(now, 'a')}</span>
          </div>
          <div className="text-xl text-slate-400 mt-2">{format(now, 'EEEE, d MMMM yyyy')}</div>
        </div>
        <button
          onClick={handleLock}
          className="text-slate-600 hover:text-slate-400 p-2 transition-colors"
          title="Lock display"
          aria-label="Lock display"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
          </svg>
        </button>
      </div>

      {/* Main status area */}
      <div className="flex-1 flex items-center justify-center px-10">
        {loading ? (
          <div className="text-slate-500">Loading…</div>
        ) : available ? (
          <div className="text-center">
            <div className="inline-flex items-center gap-4 mb-6">
              <div className="w-6 h-6 rounded-full bg-green-500 animate-pulse" />
              <div className="text-7xl font-bold text-green-400">AVAILABLE</div>
            </div>
            <div className="text-2xl text-slate-300">
              {next ? (
                <>
                  Free until <span className="font-semibold text-white">{format(toDate(next.startDate), 'h:mm a')}</span>
                  <div className="text-lg text-slate-500 mt-2">
                    Next: {firstNameLastInitial(next)} · {format(toDate(next.startDate), 'h:mm a')} – {format(toDate(next.endDate), 'h:mm a')}
                  </div>
                </>
              ) : (
                <span className="text-slate-400">Free for the rest of the day</span>
              )}
            </div>
            <div className="mt-10 inline-flex items-center gap-6 bg-white rounded-2xl p-5">
              <QRCodeSVG value={BOOKING_URL} size={140} bgColor="#ffffff" fgColor="#0f172a" level="M" />
              <div className="text-left text-slate-800">
                <div className="text-base font-semibold uppercase tracking-wider text-slate-500">Book this room</div>
                <div className="text-2xl font-bold mt-1">Scan to book</div>
                <div className="text-sm text-slate-500 mt-1">thehubcrookwell.com.au</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <div className="inline-flex items-center gap-4 mb-6">
              <div className="w-6 h-6 rounded-full bg-red-500" />
              <div className="text-7xl font-bold text-red-400">IN USE</div>
            </div>
            <div className="text-3xl font-semibold mb-2">{firstNameLastInitial(current)}</div>
            <div className="text-xl text-slate-300">
              Until <span className="font-semibold text-white">{format(toDate(current.endDate), 'h:mm a')}</span>
              <span className="text-slate-500 ml-3">
                ({minutesUntil(toDate(current.endDate))} min)
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Today's schedule */}
      <div className="px-10 pb-8">
        <div className="border-t border-slate-700 pt-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-4">Today's Schedule</h2>
          {bookings.length === 0 ? (
            <p className="text-slate-500">No bookings today.</p>
          ) : (
            <div className="space-y-2">
              {bookings.map((b) => {
                const start = toDate(b.startDate);
                const end = toDate(b.endDate);
                const isCurrent = current?.id === b.id;
                const isPast = end && end < now && !isCurrent;
                return (
                  <div
                    key={b.id}
                    className={`flex items-center gap-6 px-4 py-3 rounded-lg ${
                      isCurrent ? 'bg-red-500/10 border border-red-500/30' :
                      isPast ? 'opacity-40' : 'bg-slate-800/50'
                    }`}
                  >
                    <div className={`text-lg font-semibold tabular-nums w-40 ${isCurrent ? 'text-red-400' : 'text-slate-300'}`}>
                      {format(start, 'h:mm a')} – {format(end, 'h:mm a')}
                    </div>
                    <div className={`text-lg flex-1 ${isCurrent ? 'text-white font-semibold' : 'text-slate-400'}`}>
                      {firstNameLastInitial(b)}
                    </div>
                    {isCurrent && (
                      <span className="text-xs uppercase tracking-wider font-semibold text-red-400">Now</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MeetingRoomDisplayPage;
